import { 
  RequestTokenPayload, 
  RequestTokenResponse, 
  VerifyTokenPayload, 
  VerifyTokenResponse,
  DirectAuthPayload,
  DirectAuthResponse
} from '../types/auth.types';
import { API_ENDPOINTS, DEFAULT_HEADERS, getAuthHeaders } from '../config/api.config';

/**
 * Service d'authentification pour gérer les appels API liés à l'authentification
 */
export const authService = {
  /**
   * Demande un token d'authentification par SMS
   * @param payload Données contenant le numéro de téléphone
   * @returns Réponse de l'API avec les informations sur le token
   */
  async requestToken(payload: RequestTokenPayload): Promise<RequestTokenResponse> {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.REQUEST_TOKEN, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Une erreur est survenue lors de la demande de token');
      }
      
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('Une erreur inconnue est survenue');
    }
  },

  /**
   * Vérifie un token d'authentification
   * @param payload Données contenant le token à vérifier
   * @returns Réponse de l'API avec les informations utilisateur et le JWT
   */
  async verifyToken(payload: VerifyTokenPayload): Promise<VerifyTokenResponse> {
    try {
      // On peut utiliser soit GET avec query params, soit POST avec body selon la préférence
      const response = await fetch(`${API_ENDPOINTS.AUTH.VERIFY}?token=${payload.token}`, {
        method: 'GET',
        headers: DEFAULT_HEADERS,
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Une erreur est survenue lors de la vérification du token');
      }
      
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('Une erreur inconnue est survenue');
    }
  },

  /**
   * Authentifie directement un utilisateur
   * @param payload Données contenant les informations d'authentification
   * @returns Réponse de l'API avec les informations utilisateur et le JWT
   */
  async directAuth(payload: DirectAuthPayload): Promise<DirectAuthResponse> {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.DIRECT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Une erreur est survenue lors de l\'authentification directe');
      }
      
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('Une erreur inconnue est survenue');
    }
  },

  /**
   * Vérifie si un token JWT est stocké et valide
   * @returns true si un token est présent et valide
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;
    
    // Vérification basique de la validité du token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Si le token est marqué comme permanent, il est valide
      if (payload.permanent === true) {
        return true;
      }
      
      // Sinon, vérifier l'expiration si elle existe
      if (payload.exp) {
        const expirationTime = payload.exp * 1000; // Conversion en millisecondes
        return Date.now() < expirationTime;
      }
      
      // Si aucune information de validité n'est trouvée, considérer le token comme invalide
      return false;
    } catch (error) {
      return false;
    }
  },

  /**
   * Récupère le token JWT stocké
   * @returns Le token JWT ou null si non présent
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  },

  /**
   * Stocke le token JWT
   * @param token Le token JWT à stocker
   */
  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
  },

  /**
   * Supprime le token JWT stocké
   */
  removeToken(): void {
    localStorage.removeItem('auth_token');
  },

  /**
   * Vérifie si un numéro de téléphone est au format camerounais valide
   * @param phone Numéro de téléphone à vérifier
   * @returns true si le format est valide, false sinon
   */
  validatePhoneNumber(phone: string): boolean {
    // Formats camerounais valides:
    // 6XXXXXXXX (9 chiffres)
    // 2376XXXXXXXX (11 chiffres)
    // +2376XXXXXXXX (12 caractères avec le +)
    // 002376XXXXXXXX (13 chiffres avec le 00)
    
    // Nettoyer le numéro (supprimer les espaces)
    const cleanPhone = phone.replace(/\s/g, '');
    
    // Vérifier les différents formats
    const regexSimple = /^[6-9][0-9]{8}$/;                // Format: 6XXXXXXXX
    const regexWithPrefix = /^237[6-9][0-9]{8}$/;         // Format: 2376XXXXXXXX
    const regexWithPlusPrefix = /^\+237[6-9][0-9]{8}$/;    // Format: +2376XXXXXXXX
    const regexWithIntlPrefix = /^00237[6-9][0-9]{8}$/;    // Format: 002376XXXXXXXX
    
    return (
      regexSimple.test(cleanPhone) ||
      regexWithPrefix.test(cleanPhone) ||
      regexWithPlusPrefix.test(cleanPhone) ||
      regexWithIntlPrefix.test(cleanPhone)
    );
  },
  
  /**
   * Formate un numéro de téléphone au format international
   * @param phone Numéro de téléphone à formater
   * @returns Numéro formaté (ex: 237XXXXXXXXX)
   */
  formatPhoneNumber(phone: string): string {
    // Nettoyer le numéro (supprimer les espaces)
    const cleanPhone = phone.replace(/\s/g, '');
    
    // Extraire le numéro de base (9 chiffres) selon différents formats
    let baseNumber = '';
    
    // Format: 6XXXXXXXX (9 chiffres)
    if (/^[6-9][0-9]{8}$/.test(cleanPhone)) {
      baseNumber = cleanPhone;
    }
    // Format: 2376XXXXXXXX (11 chiffres)
    else if (/^237[6-9][0-9]{8}$/.test(cleanPhone)) {
      baseNumber = cleanPhone.substring(3);
    }
    // Format: +2376XXXXXXXX (12 caractères avec le +)
    else if (/^\+237[6-9][0-9]{8}$/.test(cleanPhone)) {
      baseNumber = cleanPhone.substring(4);
    }
    // Format: 002376XXXXXXXX (13 chiffres avec le 00)
    else if (/^00237[6-9][0-9]{8}$/.test(cleanPhone)) {
      baseNumber = cleanPhone.substring(5);
    }
    // Si le format ne correspond à aucun des formats valides, retourner tel quel
    else {
      // Enlever le + au début si présent
      const withoutPlus = cleanPhone.replace(/^\+/, '');
      // Enlever le 00 au début si présent
      const withoutIntlPrefix = withoutPlus.replace(/^00/, '');
      // Si le numéro commence par 237, le garder tel quel, sinon ajouter 237
      return withoutIntlPrefix.startsWith('237') ? withoutIntlPrefix : `237${withoutIntlPrefix}`;
    }
    
    // Retourner le numéro formaté avec le préfixe 237
    return `237${baseNumber}`;
  },
};
